import { ChatsCircle, MagnifyingGlass } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { LoadingState } from '@/components/loading-state'

import { localizedCategoryName } from '@/lib/category-labels'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'

import type { ActivityProjection, Friend } from '../api'
import type { ChatSearchPhase } from '../hooks/use-chat-search'
import { ChatAvatar } from './chat-avatar'

interface ChatSearchResultsProps {
  phase: ChatSearchPhase
  term: string
  friends: Friend[]
  activities: ActivityProjection[]
  openingFriendId: string | null
  onOpenFriend: (friend: Friend) => void
}

/**
 * Search results for UI_PARITY §3.12: friends and activities in named
 * sections. Friends are buttons because opening the conversation is a POST
 * that also unhides/unpins it; activities are direct chat links.
 */
export function ChatSearchResults({
  phase,
  term,
  friends,
  activities,
  openingFriendId,
  onOpenFriend,
}: ChatSearchResultsProps) {
  const { t } = useTranslation()
  // D-101: search only ever returns friends, so names unmask with the index.
  const formatPerson = usePersonNameFormatter()

  if (phase === 'idle') {
    return (
      <div className="chat-search__hint" role="status">
        <MagnifyingGlass size={20} weight="bold" aria-hidden="true" />
        <p>{t('chat.search.minChars')}</p>
      </div>
    )
  }

  if (phase === 'loading') {
    return (
      <LoadingState labelKey="chat.search.loading" />
    )
  }

  if (phase === 'error') {
    return (
      <div className="chat-search__hint chat-search__hint--error" role="alert">
        <p>{t('chat.search.error')}</p>
      </div>
    )
  }

  if (friends.length === 0 && activities.length === 0) {
    return (
      <div className="chat-search__hint" role="status">
        <p>{t('chat.search.empty', { term })}</p>
      </div>
    )
  }

  return (
    <div className="chat-search__results">
      {friends.length > 0 ? (
        <section aria-labelledby="chat-search-friends">
          <h2 id="chat-search-friends" className="chat-section-title">
            {t('chat.search.friends')}
          </h2>
          <ul className="chat-search__list">
            {friends.map((friend) => (
              <li key={friend.userId}>
                <button
                  type="button"
                  className="chat-search__row"
                  onClick={() => onOpenFriend(friend)}
                  disabled={openingFriendId === friend.userId}
                  aria-busy={openingFriendId === friend.userId}
                  aria-label={t('chat.search.openFriend', {
                    name: formatPerson(friend),
                  })}
                >
                  <ChatAvatar
                    name={formatPerson(friend)}
                    src={friend.avatarUrl}
                    size="md"
                  />
                  <span className="chat-search__body">
                    <span className="chat-search__title">
                      {formatPerson(friend)}
                    </span>
                    <span className="chat-search__subtitle">
                      @{friend.nickname}
                    </span>
                  </span>
                  <span className="chat-search__action">
                    {t('chat.search.openFriend', { name: formatPerson(friend) })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {activities.length > 0 ? (
        <section aria-labelledby="chat-search-activities">
          <h2 id="chat-search-activities" className="chat-section-title">
            {t('chat.search.activities')}
          </h2>
          <ul className="chat-search__list">
            {activities.map((activity) => (
              <li key={activity.id}>
                <Link
                  to={`/chat/${activity.id}`}
                  className="chat-search__row"
                  aria-label={t('chat.search.openActivity', {
                    title: activity.title,
                  })}
                >
                  <span className="chat-search__activity-icon" aria-hidden="true">
                    <ChatsCircle size={18} weight="bold" />
                  </span>
                  <span className="chat-search__body">
                    <span className="chat-search__title">{activity.title}</span>
                    <span className="chat-search__subtitle">
                      {[activity.city, localizedCategoryName(t, activity.categorySlug, activity.categoryName)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="chat-search__action">
                    {t('chat.search.openChat')}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
